import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Sparkles,
  Brain,
  Palette,
  Image as ImageIcon,
  TrendingUp,
  Users,
  Heart,
  Target,
  ArrowRight,
  CheckCircle2,
  Circle,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import StorageMigrationBanner from "@/components/StorageMigrationBanner";

const CHANNEL_COLORS = ["#F97316", "#F43F5E", "#F59E0B", "#22D3EE", "#A78BFA"];

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/dashboard/overview")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;

  const stats = data.stats;
  const m = data.metrics;
  const biz = data.business;

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-orange-400/80 mb-2">
            Welcome back
          </div>
          <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight" data-testid="dashboard-title">
            {biz?.business_name || user?.name}
          </h1>
          <p className="text-white/50 mt-2 text-sm">
            {biz?.industry} · {biz?.country} {biz?.city ? `· ${biz.city}` : ""}
          </p>
        </div>
        <Link
          to="/dashboard/content"
          data-testid="dashboard-quick-create"
          className="inline-flex items-center gap-2 brand-gradient px-5 py-3 rounded-lg text-sm font-semibold hover:opacity-90 glow-orange self-start"
        >
          <Sparkles className="h-4 w-4" /> Generate content
        </Link>
      </header>

      <StorageMigrationBanner />

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={TrendingUp} label="Monthly reach" value={m.reach.toLocaleString()} delta="+12.4%" testid="kpi-reach" />
        <Kpi icon={Heart} label="Engagement" value={`${m.engagement_rate}%`} delta="+0.8%" testid="kpi-engagement" />
        <Kpi icon={Users} label="Followers" value={m.followers.toLocaleString()} delta="+342" testid="kpi-followers" />
        <Kpi icon={Target} label="Leads" value={m.leads} delta="+22" testid="kpi-leads" />
      </div>

      {/* charts row */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 glass rounded-2xl p-6">
          <div className="flex items-baseline justify-between mb-5">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-white/40">Traffic — last 7 days</div>
              <div className="font-heading text-2xl font-black mt-1">5,150 visits</div>
            </div>
            <div className="text-xs text-green-400 font-mono">+18.2% WoW</div>
          </div>
          <div className="h-56">
            <ResponsiveContainer>
              <AreaChart data={m.weekly_traffic}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F97316" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#F97316" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" stroke="#71717A" fontSize={11} />
                <YAxis stroke="#71717A" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0A0A10", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                <Area type="monotone" dataKey="visits" stroke="#F97316" fill="url(#g1)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass rounded-2xl p-6">
          <div className="text-xs uppercase tracking-[0.25em] text-white/40">Channel mix</div>
          <div className="font-heading text-2xl font-black mt-1 mb-4">By share</div>
          <div className="h-56">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={m.channels} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={3}>
                  {m.channels.map((_, i) => (
                    <Cell key={i} fill={CHANNEL_COLORS[i % CHANNEL_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "#0A0A10", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
            {m.channels.map((c, i) => (
              <div key={c.name} className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: CHANNEL_COLORS[i % CHANNEL_COLORS.length] }} />
                <span className="text-white/60">{c.name}</span>
                <span className="ml-auto text-white/40 font-mono">{c.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Setup checklist + quick actions */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 glass rounded-2xl p-6">
          <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-1">Brand setup</div>
          <h3 className="font-heading text-xl font-bold mb-5">Get fully launched</h3>
          <ul className="space-y-3">
            <Step done label="Business onboarded" />
            <Step done={stats.has_analysis} label="AI business analysis" to="/dashboard/analysis" />
            <Step done={stats.has_brand} label="Brand identity created" to="/dashboard/brand" />
            <Step done={stats.logos_generated > 0} label="First logo generated" to="/dashboard/logos" />
            <Step done={stats.content_generated > 0} label="First piece of content" to="/dashboard/content" />
          </ul>
        </div>

        <div className="glass rounded-2xl p-6">
          <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-1">Quick actions</div>
          <h3 className="font-heading text-xl font-bold mb-5">Run a play</h3>
          <div className="grid gap-3">
            <QuickLink to="/dashboard/analysis" icon={Brain} label="Generate AI Analysis" />
            <QuickLink to="/dashboard/content" icon={Sparkles} label="Write a social post" />
            <QuickLink to="/dashboard/brand" icon={Palette} label="Build brand identity" />
            <QuickLink to="/dashboard/logos" icon={ImageIcon} label="Generate a logo" />
          </div>
        </div>
      </div>

      {/* Connected accounts */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-baseline justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-white/40">Connected accounts</div>
            <h3 className="font-heading text-xl font-bold mt-1">Channels (mock for MVP)</h3>
          </div>
          <span className="text-xs text-white/40 font-mono">OAuth coming soon</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {data.connected_accounts.map((a) => (
            <div key={a.platform} className="border border-white/10 rounded-lg p-4 hover:bg-white/[0.03] transition">
              <div className="text-sm font-semibold">{a.platform}</div>
              <div className="text-xs text-white/50 mt-1">{a.connected ? "Connected" : "Not connected"}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, delta, testid }) {
  return (
    <div className="glass rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.25em] text-white/40">{label}</div>
        <Icon className="h-4 w-4 text-orange-400" />
      </div>
      <div className="font-heading text-3xl font-black mt-3">{value}</div>
      <div className="text-xs text-green-400 mt-1 font-mono">{delta}</div>
    </div>
  );
}

function Step({ done, label, to }) {
  const inner = (
    <div className="flex items-center justify-between p-3 rounded-lg hover:bg-white/[0.03] transition border border-white/5">
      <div className="flex items-center gap-3">
        {done ? (
          <CheckCircle2 className="h-5 w-5 text-orange-500" />
        ) : (
          <Circle className="h-5 w-5 text-white/30" />
        )}
        <span className={`text-sm ${done ? "line-through text-white/50" : ""}`}>{label}</span>
      </div>
      {!done && to && <ArrowRight className="h-4 w-4 text-white/40" />}
    </div>
  );
  return to && !done ? (
    <Link to={to} className="block">
      {inner}
    </Link>
  ) : (
    inner
  );
}

function QuickLink({ to, icon: Icon, label }) {
  return (
    <Link
      to={to}
      data-testid={`quicklink-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="flex items-center gap-3 p-3 rounded-lg border border-white/10 hover:bg-white/5 hover:border-orange-500/30 transition group"
    >
      <Icon className="h-4 w-4 text-orange-400" />
      <span className="text-sm flex-1">{label}</span>
      <ArrowRight className="h-4 w-4 text-white/30 group-hover:text-white" />
    </Link>
  );
}

function Loader() {
  return (
    <div className="space-y-4">
      <div className="h-10 w-72 bg-white/5 rounded animate-pulse" />
      <div className="grid grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-28 bg-white/5 rounded-2xl animate-pulse" />
        ))}
      </div>
      <div className="h-64 bg-white/5 rounded-2xl animate-pulse" />
    </div>
  );
}
